import { useState, useEffect } from 'react';
import './News.css';
import { NewsItem, Resource } from './Models/NewsModels.tsx';
import { Helmet } from 'react-helmet-async';
import { Config } from '../../config.tsx';

export const News = () => {
    const [newsData, setNewsData] = useState<NewsItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const response = await fetch(`${Config.API_URL}/news`);
                if (!response.ok) {
                    throw new Error('Problem z pobraniem danych newsów: ' + (await response.json()).error);
                }
                const data = await response.json();
                // Sortuj wiadomości według daty (od najnowszej)
                data.sort((a: NewsItem, b: NewsItem) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                setNewsData(data);
                setLoading(false);
            } catch (error) {
                console.error('Błąd podczas pobierania danych:', error);
                setLoading(false);
            }
        };

        fetchNews().then(() => console.log("Fresh newses"));
    }, []);

    // Funkcja generująca dynamiczny tytuł i opis dla SEO
    const getMetaTitle = () => {
        if (selectedNews) {
            return `${selectedNews.header} | Aktualności Saldo.pl`;
        }
        return 'Aktualności i zmiany w przepisach | Biuro rachunkowe Saldo.pl';
    };

    const getMetaDescription = () => {
        if (selectedNews) {
            return selectedNews.description;
        }
        return 'Najnowsze aktualności i informacje z zakresu podatków, księgowości i finansów. Bądź na bieżąco z ważnymi zmianami w przepisach podatkowych.';
    };

    const getCanonicalUrl = () => {
        if (selectedNews) {
            const slug = selectedNews.header
                .toLowerCase()
                .replace(/[^\w\s]/gi, '')
                .replace(/\s+/g, '-');
            return `https://podatki-saldo.pl/news/${slug}`;
        }
        return 'https://podatki-saldo.pl/news';
    };

    const openNewsReader = (news: NewsItem) => {
        setSelectedNews(news);
        window.scrollTo({ top: 0, behavior: 'auto' });
    };

    const closeNewsReader = () => {
        setSelectedNews(null);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const renderResource = (resource: Resource) => {
        const baseUrl = '/images/'; // Zakładam, że pliki będą w tym folderze

        switch (resource.type) {
            case 'IMAGE':
                return (
                    <div key={resource.name} className="news-resource news-image">
                        <img src={baseUrl + resource.name} alt={resource.name} />
                    </div>
                );
            case 'VIDEO':
                return (
                    <div key={resource.name} className="news-resource news-video">
                        <video controls>
                            <source src={baseUrl + resource.name} type="video/mp4" />
                            Twoja przeglądarka nie obsługuje odtwarzania wideo.
                        </video>
                    </div>
                );
            case 'FILE':
                return (
                    <div key={resource.name} className="news-resource news-file">
                        <a href={baseUrl + resource.name} download>
                            <span className="file-name">{resource.name}</span>
                            <i className="download-arrow"></i>
                        </a>
                    </div>
                );
            default:
                return null;
        }
    };

    // Funkcja grupująca zasoby według typu
    const groupResourcesByType = (resources: Resource[]) => {
        const videos = resources.filter(r => r.type === 'VIDEO');
        const images = resources.filter(r => r.type === 'IMAGE');
        const files = resources.filter(r => r.type === 'FILE');

        return { videos, images, files };
    };

    // Renderowanie zgrupowanych zasobów
    const renderGroupedResources = (resources: Resource[]) => {
        if (!resources || resources.length === 0) return null;

        const { videos, images, files } = groupResourcesByType(resources);

        return (
            <div className="news-resources-grouped">
                {videos.length > 0 && (
                    <div className="videos-container">
                        {videos.map((resource) => renderResource(resource))}
                    </div>
                )}

                {images.length > 0 && (
                    <div className="images-container">
                        {images.map((resource) => renderResource(resource))}
                    </div>
                )}

                {files.length > 0 && (
                    <div className="files-container">
                        {files.map((resource) => renderResource(resource))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="main-content">
            <Helmet>
                <title>{getMetaTitle()}</title>
                <meta name="description" content={getMetaDescription()} />
                <link rel="canonical" href={getCanonicalUrl()} />
            </Helmet>

            <div className="news-main">
                <h1 className="news-title">Aktualności</h1>

                {loading ? (
                    <div className="loading-spinner">Ładowanie...</div>
                ) : (
                    <>
                        {/* Reader - wyświetla się tylko gdy wybrano news */}
                        {selectedNews && (
                            <div className="news-reader">
                                <div className="news-reader-content">
                                    <div className="news-date">{formatDate(selectedNews.date)}</div>
                                    <h2 className="news-header">{selectedNews.header}</h2>

                                    {selectedNews.resources && selectedNews.resources.length > 0 &&
                                        renderGroupedResources(selectedNews.resources)
                                    }

                                    <div className="news-full-content">
                                        <p>{selectedNews.content_text}</p>
                                    </div>

                                    <button
                                        className="news-toggle-button"
                                        onClick={closeNewsReader}
                                    >
                                        Powrót
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Widok wszystkich newsów - widoczny tylko gdy żaden news nie jest wybrany */}
                        {!selectedNews && (
                            <>
                                {/* Sekcja z najnowszymi wiadomościami */}
                                <div className="latest-news-section">
                                    <div className="latest-news-grid">
                                        {newsData.slice(0, 3).map((news, index) => (
                                            <div key={`latest-${index}`} className="latest-news-item">
                                                <div className="latest-news-date">{formatDate(news.date)}</div>
                                                <h3 className="latest-news-header">{news.header}</h3>
                                                <div className="latest-news-description">{news.description}</div>

                                                <button
                                                    className="news-toggle-button"
                                                    onClick={() => openNewsReader(news)}
                                                >
                                                    Czytaj więcej
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Lista pozostałych wiadomości (bez 3 najnowszych) */}
                                {newsData.length > 3 && (
                                    <div className="news-container">
                                        {newsData.slice(3).map((news, index) => {
                                            const actualIndex = index + 3;
                                            return (
                                                <div
                                                    key={`rest-${actualIndex}`}
                                                    className="news-item"
                                                >
                                                    <div className="news-date">{formatDate(news.date)}</div>
                                                    <h2 className="news-header">{news.header}</h2>



                                                    <div className="news-content">
                                                        <div className="news-description">
                                                            {news.description}
                                                        </div>
                                                    </div>

                                                    <button
                                                        className="news-toggle-button"
                                                        onClick={() => openNewsReader(news)}
                                                    >
                                                        Czytaj więcej
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
